import { Injectable, Signal, WritableSignal, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { Observable, catchError, combineLatest, debounceTime, distinctUntilChanged, filter, map, of, shareReplay, switchMap, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { ICommercialSale, ICommercialSales } from "src/models/commercial/ICommercialSale";
import { ICommercialSaleFilter, ILatLongBounds } from "src/models/commercial/ICommercialSaleFilter";
import { IMiniInfocardCommercial } from "src/models/commercial/IMiniInfocardDataCommercial.model";
import { ImagePackage } from "src/models/common/image.model";
import { MapMarkerType } from "src/models/map/map-marker-type.enum";
import { CommercialFilterStore } from "src/stores/commercial-filter.store";
import { ConvertUtil } from "src/util/tools/convert.util";
import { HttpDataService, IResponseWrapper } from "../httpdata.service";
import { MapActionsService } from "../map/map-actions.service";
import { IMapUpdate, MapMode, MapStateService } from "../map/map-state.service";
import { ResourceService } from "../resources/resource.service";

@Injectable()
export class CommercialSalesDataService {
    private readonly filterStore = inject(CommercialFilterStore);
    private readonly mapStateService = inject(MapStateService);
    private readonly http = inject(HttpDataService);
    private readonly mapActionsService = inject(MapActionsService);
    private readonly selectedSale: WritableSignal<IMiniInfocardCommercial> = signal<IMiniInfocardCommercial>(undefined);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly resourceService = inject(ResourceService);

    private readonly filterValue$ = toObservable(this.filterStore.filterValue);

    selectedSale$ = toObservable(this.selectedSale);
    sales$: Observable<ICommercialSales> = combineLatest([this.mapStateService.mapUpdateBoundsOrPolygons$, this.filterValue$, this.mapStateService.mapMode$]).pipe(
        debounceTime(300),
        filter(([mapState, filter, mapMode]) => mapState.state.bounds !== null && mapMode === MapMode.CommercialSale),
        switchMap(([mapState, filter, mapMode]) => this.getSales(filter, mapState)),
        // shareReplay never releases its source subscription, so without this the pipeline keeps
        // running off the root-scoped map state after this route's injector is destroyed.
        takeUntilDestroyed(),
        shareReplay(1)
    );
    selectedUnitAndSale$: Observable<IMiniInfocardIdentifier> = this.route.queryParams.pipe(
        catchError((err) => {
            return of({ unitId: null, commercialSaleId: null, estateSaleId: null });
        }),
        map((params: Params) => {
            let commercialSaleId = null;
            let unitId = null;
            let estateSaleId = null;

            if (params["unitID"] ?? params["unitId"]) {
                unitId = params["unitID"] ?? params["unitId"];
            }
            if (params["commercialSaleId"]) {
                commercialSaleId = params["commercialSaleId"];
            }
            if (params["estateSaleId"]) {
                estateSaleId = params["estateSaleId"];
            }
            return { unitId: unitId, commercialSaleId: commercialSaleId, estateSaleId: estateSaleId };
        }),
        distinctUntilChanged((prev, next) => {
            const changed = prev?.unitId === next?.unitId && prev?.commercialSaleId === next?.commercialSaleId && prev.estateSaleId === next.estateSaleId;
            return changed;
        }),
        tap((x) => {})
    );
    private readonly activeSale$ = this.selectedUnitAndSale$.pipe(
        switchMap((id) => {
            if (!id.commercialSaleId && !id.unitId && !id.estateSaleId) {
                return of(null);
            } else {
                return this.getSale(id).pipe(
                    map((x) => {
                        if (x === null) {
                            return null;
                        }
                        x.estateTypeCssString = ConvertUtil.estateTypeToCssString(x.estateType);
                        return x;
                    })
                );
            }
        }),
        catchError((err) => {
            console.log("active sale err", err);
            return of(null);
        }),
        tap((x) => {
            if (x) {
                this.mapActionsService.addCurrentMarker(x);
            } else {
                this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.SelectedSale);
                this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.Outline);
            }
        }),
        // Keeps the marker side effects above from running once this route's injector is gone.
        takeUntilDestroyed(),
        shareReplay(1)
    );

    readonly activeSale: Signal<IMiniInfocardCommercial> = toSignal(this.activeSale$);

    setSelectedSale(sale: ICommercialSale | IMiniInfocardCommercial): void {
        let queryParams: IMiniInfocardIdentifier = { commercialSaleId: null, unitId: null, estateSaleId: null };
        if (sale) {
            queryParams.commercialSaleId = sale.commercialEstateSaleId;
            queryParams.unitId = sale.unitId;
            queryParams.estateSaleId = sale.estateSaleId;
        }
        this.router.navigate(["/map/commercial/market"], {
            relativeTo: this.route,
            queryParams: queryParams,
            queryParamsHandling: "merge",
        });
    }
    imagePackages$: Observable<ImagePackage[]> = this.activeSale$.pipe(
        switchMap((sale) => {
            if (sale == null) {
                return of<ImagePackage[]>([]);
            }
            if (sale.imagePackages[0]?.images?.length > 0) {
                return of(sale.imagePackages);
            }
            return this.resourceService.getBlomImagePackage({ lat: sale.decimalLatitude, lng: sale.decimalLongitude });
        }),
        shareReplay(1)
    );
    readonly imagePackages = toSignal(this.imagePackages$);

    private getSales(filter: ICommercialSaleFilter, mapBounds: IMapUpdate): Observable<ICommercialSales> {
        const northEast = mapBounds.state.bounds.getNorthEast();
        const southWest = mapBounds.state.bounds.getSouthWest();

        const _filter: ICommercialSaleFilterParams = { ...filter };

        _filter.latLongBounds = { latMin: null, latMax: null, longMin: null, longMax: null };
        _filter.latLongBounds.latMin = southWest.lat;
        _filter.latLongBounds.latMax = northEast.lat;
        _filter.latLongBounds.longMin = southWest.lng;
        _filter.latLongBounds.longMax = northEast.lng;
        _filter.simplifiedSearch = mapBounds.state.zoom <= this.mapStateService.zoomLimitCommercial;
        _filter.estateSubTypes = this.mergeSubtypes(_filter.agriculturalSubTypes, _filter.commercialSubTypes);
        _filter.estateTypes = _filter.otherEstateTypes;
        let observable = this.http.post<ICommercialSales>(`${environment.apiUrl}/Map/SalesInAreaCommercial`, _filter).pipe(
            catchError((err) => {
                console.log("err" + err);
                return of({ data: [] });
            }),
            map((res) => {
                res.data.map((x) => {
                    x.estateTypeCssString = ConvertUtil.estateTypeToCssString(x.estateType);
                });
                let sorted = res.data.sort((a, b) => (a.informationDate < b.informationDate ? 1 : b.informationDate < a.informationDate ? -1 : 0));
                return { simplifiedSearch: _filter.simplifiedSearch, data: sorted };
            })
        );
        return observable;
    }

    getImage(imageUrl: string): Observable<File> {
        return this.http.get<File>(`${environment.apiUrl}imageUrl`);
    }

    private getSale(sale: IMiniInfocardIdentifier): Observable<IMiniInfocardCommercial> {
        let url = `${environment.apiUrl}/Map/CommercialMiniInfocardData?`;
        const params = [];
        if (sale.commercialSaleId) {
            params.push(`commercialSaleId=${sale.commercialSaleId}`);
        }
        if (sale.estateSaleId) {
            params.push(`estateSaleId=${sale.estateSaleId}`);
        }

        //Dersom vi har estateSaleId eller commercialSaleId, så sender vi ikke med unitId,
        //da trenger vi unitId bare for å finne riktig unit dersom det er flere units i salget
        if (!sale.commercialSaleId && !sale.estateSaleId && sale.unitId) {
            params.push(`unitId=${sale.unitId}`);
        }
        url += params.join("&");
        return this.http.get<IResponseWrapper<IMiniInfocardCommercial[]>>(url).pipe(
            map((x) => this.getCorrectAndMapConnected(x?.data, sale.unitId)),
            catchError((err) => {
                console.log("getSale err" + err);
                return of(null);
            })
        );
    }
    private getCorrectAndMapConnected(data: IMiniInfocardCommercial[], unitId?: number): IMiniInfocardCommercial {
        if (!data || data.length === 0) return null;

        if (data[0].imagePackages) {
            data[0].imagePackages.forEach((imagePackage: any) => {
                if (imagePackage.images && imagePackage.images.length > 0) {
                    imagePackage.images.forEach((image: any) => {
                        image.imageUrl = this.resourceService.getImageUrl(image.imageId);
                        image.imageThumbnailUrl = this.resourceService.getImageUrl(image.imageId, true);
                    });
                }
            });
        }
        if (data.length === 1 || !unitId) return data[0];

        let index = 0;
        const unitIdNumber = Number(unitId);
        if (!isNaN(unitIdNumber)) {
            const saleWithUnitId = data.findIndex((x) => x.unitId === unitIdNumber);
            if (saleWithUnitId !== -1) {
                index = saleWithUnitId;
            }
        }
        let sale = data[index];
        for (let i = 0; i < data.length; i++) {
            sale.linkedSales ??= [];
            if (i !== index) {
                sale.linkedSales.push(data[i]);
            }
        }
        if (sale.linkedSales.length > 0) {
            sale.linkedSales = this.toSorted(sale.linkedSales, "cadastre");
        }

        return sale;
    }
    mergeSubtypes(agriculturalSubTypes: number[], commercialSubTypes: number[]): number[] {
        const mergedSubtypes = [];
        if (agriculturalSubTypes) {
            mergedSubtypes.push(...agriculturalSubTypes);
        }
        if (commercialSubTypes) {
            mergedSubtypes.push(...commercialSubTypes);
        }
        return mergedSubtypes;
    }

    private toSorted<T>(data: T[], paramName: keyof T): T[] {
        return data.sort((a, b) => {
            if (a[paramName] < b[paramName]) {
                return 1;
            } else if (b[paramName] < a[paramName]) {
                return -1;
            } else {
                return 0;
            }
        });
    }
}
export interface IMiniInfocardIdentifier {
    unitId?: number;
    commercialSaleId?: number;
    estateSaleId?: number;
}
export interface ICommercialSaleFilterParams extends ICommercialSaleFilter {
    estateSubTypes?: number[];
    estateTypes?: number[];
    latLongBounds?: ILatLongBounds;
}
